import React from 'react';
import _ from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import useUI from 'hooks/use-ui';
import { CategoriesWithPotencies } from 'shared/constants';
import EmptyLoadingState from 'components/empty-loading-state';
import { TwoColumnSidebar } from 'components/layouts/two-column';
import { useStickySidebar } from 'hooks/use-sticky-sidebar';
import BrandsFilter from './brands-filter';
import PotencyFilter from './potency-filter';
import StrainTypeFilter from './strain-type-filter';
import SubcategoryFilter from './subcategory-filter';
import WeightFilter from './weight-filter';
import { TerpenesFilter } from './terpenes-filter';
import { CategorySubcategoryFilter } from './category-subcategory-filter';
import { EffectsFilter } from './effects-filter';
import { SpecialsFilter } from './specials-filter';
import { SubcategoriesFilterMultiSelect } from './subcategory-filter/subcategory-filter-multi-select';

export default function Sidebar({
  menuFilterOptions,
  category,
  isLoading,
  route,
  dispensary,
  onSearchPage,
  onCollectionsPage,
  customCategory,
}) {
  const flags = useFlags();
  const UI = useUI();
  const isStoreFront = useObserver(() => UI.isStoreFront);
  const isOnSubcategoryPage = useObserver(() => UI.onSubcategoryPage);
  const { sidebarRef, sidebarStyle } = useStickySidebar({ disableSticky: !isStoreFront });

  const { brands, categoriesSubcategories, subcategories, strainTypes, weights, effects, terpenes } = menuFilterOptions;
  const onBrandsPage = _.includes(route, 'brands');
  const hidePotencyFilters = _.get(dispensary, `storeSettings.hideFilters`, false);
  const showSubcategories = !_.isEmpty(subcategories) && !onBrandsPage;
  const showWeights = weights?.length > 1 && !onBrandsPage;
  const showStrainTypes = !_.isEmpty(strainTypes) && !onBrandsPage;
  const showPotencies = !hidePotencyFilters && _.includes(CategoriesWithPotencies, category?.key) && !onBrandsPage;
  const showBrands = !_.isEmpty(brands);
  const showEffects = !_.isEmpty(effects) && !onBrandsPage && !_.get(dispensary, `storeSettings.hideEffects`, false);
  const showTerpenes = !_.isEmpty(terpenes);
  const isMultiSelectSubcategoryFilterEnabled = flags['ecomm.multi-filter-subcategories.rollout'] ?? false;
  const showSpecialsFilter = flags['ecomm.specials-redesign.rollout'] ?? false;
  const onAllProductsPage = onSearchPage;

  const hideCategorySubcategoryFilter = onAllProductsPage || onCollectionsPage;
  const hideSubcategoryFilter = showSubcategories && !onAllProductsPage && !onCollectionsPage;
  const showSubcategoriesFilterMultiSelect = isMultiSelectSubcategoryFilterEnabled && !isOnSubcategoryPage;

  return (
    <TwoColumnSidebar ref={sidebarRef} $stickyStyle={sidebarStyle}>
      <EmptyLoadingState isLoading={isLoading} page='filterSideBar' delay={500}>
        {!customCategory && (
          <>
            {hideCategorySubcategoryFilter && (
              <CategorySubcategoryFilter categoriesSubcategories={categoriesSubcategories} />
            )}
            {hideSubcategoryFilter &&
              (showSubcategoriesFilterMultiSelect ? (
                <SubcategoriesFilterMultiSelect subcategories={subcategories} />
              ) : (
                <SubcategoryFilter category={category} subcategories={subcategories} />
              ))}
          </>
        )}
        {showSpecialsFilter && <SpecialsFilter />}
        {showWeights && <WeightFilter weights={weights} />}
        {showBrands && <BrandsFilter brands={brands} />}
        {showStrainTypes && <StrainTypeFilter strainTypes={strainTypes} />}
        {showPotencies && <PotencyFilter category={category} />}
        {showEffects && <EffectsFilter effects={effects} />}
        {showTerpenes && <TerpenesFilter terpenes={terpenes} />}
      </EmptyLoadingState>
    </TwoColumnSidebar>
  );
}
